import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { Message } from '../tools/http/message';
import { MessageSeverity } from '../tools/http/message-severity';
import { CustomSnackBarComponent } from '../tools/ui/custom-snackbar/custom-snackbar.component';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { CoreData } from '../tools/core-data/core-data';
import * as _ from 'lodash';
import { ENavigationRoute } from '../model/enum/navigation-route';
import { environment } from 'src/environments/environment';
import { ENavigationErrorType } from '../model/enum/navigation-error-type';
import { MatSnackBar } from '@angular/material/snack-bar';

export class BaseService {

    constructor(protected router: Router,
        protected coreData: CoreData,
        protected snackBar: MatSnackBar) {
    }

    protected getFingerPrintGuid() {
        const nav = window.navigator;
        // const screen = window.window.screen;
        let guid = nav.mimeTypes.length + '';
        guid += nav.userAgent.replace(/\D+/g, '');
        guid += nav.plugins.length;
        // guid += screen.height || '';
        // guid += screen.width || '';
        guid += screen.pixelDepth || '';

        return guid;
    }

    private get BearerAuthorization() {
        return 'Bearer ' + this.coreData.Token;
    }

    protected generateHeader() {

        const headers: HttpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': this.BearerAuthorization,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers };
        return options;
    }

    protected generateHeaderWithBearer(bearer: string) {

        const headers: HttpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            // 'Authorization': `Bearer f7a4a548-bf91-4597-93c4-544af1b32ce4` // CONFIG
            // 'Authorization': `Bearer acf7def1-9f79-466b-a0b3-bb6469b8ca33` // UAT
            // 'Authorization': `Bearer 80191cba-6f49-49aa-97e7-8d63a55256b3` // TEST
            'Authorization': `Bearer ${bearer}`,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers };
        return options;
    }

    protected generateHeaderWithParameters(params: HttpParams) {

        const headers: HttpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': this.BearerAuthorization,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers, params };
        return options;
    }

    // 
    protected generateHeaderWithParametersWithBearer(params: HttpParams, bearer: string) {

        const headers: HttpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': `Bearer ${bearer}`,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers, params };
        return options;
    }

    protected generateHeaderForFormData() {

        const headers: HttpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': this.BearerAuthorization,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers, reportProgress: true };
        return options;
    }

    protected generateHeaderForFileDownload(bearer?: string) {

        const headers: HttpHeaders = new HttpHeaders({
            'Application': environment.appCode,
            'Scope': this.coreData.SelectedScopeCode ? this.coreData.SelectedScopeCode : '',
            'Authorization': bearer ? `Bearer ${bearer}` : this.BearerAuthorization,
            'ImpersonateAs': this.coreData.ImpersonateAs ? this.coreData.ImpersonateAs : ''
        });
        const options = { withCredentials: true, headers: headers, responseType: 'blob' as 'blob', observe: 'response' as 'body' };

        return options;
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    protected handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            let msg;
            switch (error.status) {
                case 400:
                    let txt = 'Mauvaise Action';
                    if (error.error && error.error.length > 0) {
                        txt = `${_.join(error.error, '\n')}`;
                    }
                    msg = new Message(txt, MessageSeverity.Error);
                    break;
                case 401:
                    msg = new Message('Puid ou mot de passe incorrect', MessageSeverity.Warning);
                    this.coreData.Token = null;
                    this.router.navigate([ENavigationRoute.Login]);
                    break;
                case 403:
                    this.router.navigate([ENavigationRoute.Error, { type: ENavigationErrorType.UnAuthorized }]);
                    break;
                case 404:
                    msg = new Message('Aucune donnée trouvée', MessageSeverity.Error);
                    break;
                case 413:
                    msg = new Message('Quantité de donnée maximum depassée', MessageSeverity.Error);
                    break;
                case 429:
                    msg = new Message('Trop de requêtes ont été envoyées. Vous devez attendre ' + error.error, MessageSeverity.Error);
                    break;
                case 0:
                    msg = new Message('Pas de réseau', MessageSeverity.Error);
                    break;
                default:
                    msg = new Message(error.message, MessageSeverity.Error);
            }

            if (this.snackBar) {
                this.snackBar.openFromComponent(CustomSnackBarComponent, { data: msg, horizontalPosition: 'center' });
            }

            console.error(error); // log to console instead
            throw error;
            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }
}
